import { Flight } from '../types';
import { formatRefuelLine } from './flightForm';

/** Единица налёта у числа. Подпись поля/колонки — MH_LABEL. */
export const MH_UNIT = 'м/ч';
export const MH_LABEL = 'Моточасы';

/** Значения налёта/тахометра: до сотых, без хвостового нуля (1.5, не 1.50; 1.55 как есть). */
export function formatHours(value: number): string {
  if (!Number.isFinite(value)) return '';
  return parseFloat(value.toFixed(2)).toString();
}

/** Показание тахометра (прибор). Не счётчик борта и не налёт рейса. */
export const flightTachValue = (flight: Flight) =>
  flight.tach_end ?? flight.hobbs_end;

/**
 * Налёт этого рейса в м/ч (дельта).
 * Не путать с motohours_end — накопленными моточасами борта.
 */
export const flightDurationMh = (flight: Pick<Flight, 'hours'>): number =>
  Number(flight.hours) || 0;

/**
 * Накопленные моточасы борта после рейса.
 * Не путать с hours (налёт рейса) и с hobbs_end/tach_end (тахометр в API).
 */
export const flightMotohoursEnd = (
  flight: Pick<Flight, 'motohours_end' | 'hobbs_end' | 'tach_end'>
): number => {
  if (flight.motohours_end != null && Number.isFinite(Number(flight.motohours_end))) {
    return Number(flight.motohours_end);
  }
  // Старый ответ без motohours_end: hobbs_end ещё был наработкой.
  if (flight.tach_end == null) {
    return Number(flight.hobbs_end) || 0;
  }
  return 0;
};

const display = (value: number | string | null | undefined, fallback = '—') => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'number') return formatHours(value);
  return String(value);
};

const fillUpTotal = (flight: Flight) => {
  const rows = flight.refuels || [];
  if (rows.length === 0) return null;
  const total = rows.reduce((sum, item) => {
    const cost =
      item.cost != null
        ? Number(item.cost)
        : item.liters != null && item.price_per_liter != null
          ? Number(item.liters) * Number(item.price_per_liter)
          : 0;
    return sum + (Number.isFinite(cost) ? cost : 0);
  }, 0);
  return total > 0 ? total : null;
};

export const flightDetailRows = (
  flight: Flight
): { label: string; value: string }[] => {
  const refuels = flight.refuels || [];
  const fuelRows =
    refuels.length > 0
      ? refuels.map((item, index) => ({
          label: refuels.length > 1 ? `Заправка ${index + 1}` : 'Заправка',
          value: formatRefuelLine(item),
        }))
      : [
          { label: 'Топливо', value: display(flight.fuel) },
          { label: 'Где залито', value: display(flight.fuel_location) },
        ];
  const purchase = fillUpTotal(flight);
  return [
    { label: MH_LABEL, value: display(flightDurationMh(flight)) },
    { label: 'Тахометр', value: display(flightTachValue(flight)) },
    { label: 'Тип полёта', value: flight.flight_type || 'По кругам' },
    { label: 'Посадки', value: display(flight.landings ?? 0) },
    ...fuelRows,
    { label: 'Масло', value: display(flight.oil) },
    { label: 'Расход, л', value: display(flight.fuel_used_liters) },
    {
      label: purchase != null ? 'Сумма заправок, ₽' : 'Стоимость, ₽',
      value:
        purchase != null
          ? String(purchase)
          : flight.fuel_cost != null
            ? String(flight.fuel_cost)
            : '—',
    },
  ];
};
